import { FetchBaseQueryErrorType, OrderResponse } from '@afterpaytouch/portal-api'
import {
  SUP_KEYS,
  isConflictError,
  isCreatingTooEarly,
  isCreatingTooLate,
  isDuplicatedEntityError,
  isExternalDisputeExistError,
  isInvalidAmountError,
  isTimesLimitError,
} from '../../model'

export const regularOrderLowerBound = 604800000 // 7 * 24 * 3600 * 1000
export const regularOrderUpperBound = 10368000000 // 120 * 24 * 3600 * 1000
export const supOrderLowerBound = 0
export const supOrderUpperBound = 2592000000 // 30 * 24 * 3600 * 1000

export const isSupOrder = (order: OrderResponse | null): boolean => SUP_KEYS.includes(order?.source ?? '')
export const checkCreateDisputeLowerBound = (order: OrderResponse): boolean => {
  const times = Date.now() - order.orderDate
  return times >= (isSupOrder(order) ? supOrderLowerBound : regularOrderLowerBound)
}

export const checkCreateDisputeUpperBound = (order: OrderResponse): boolean => {
  const times = Date.now() - order.orderDate
  return times <= (isSupOrder(order) ? supOrderUpperBound : regularOrderUpperBound)
}

export const checkCreateDisputeRefundableAmount = (order: OrderResponse): boolean => {
  return Number(order?.orderSummary?.totalAfterRefund?.amount) > 0
}

export const checkAmount = (amount: number): boolean => {
  return /^\d+(\.\d{1,2})?$/.test(String(amount))
}

export const isDateInvalid = (date: string | null, order: OrderResponse): boolean => {
  if (!date) {
    return true
  }

  const disputeDate = new Date(`${date} 23:59:59.999`)
  const disputeTimestamp = disputeDate.getTime()
  const today = new Date()
  today.setHours(23, 59, 59, 999) // Set to end of today

  // Check if date is after today
  if (disputeTimestamp > today.getTime()) {
    return true
  }

  // Check if date is before order date
  if (Boolean(order?.orderDate) && disputeTimestamp < order.orderDate) {
    return true
  }

  return false
}

export const getDisputeErrorMessage = (error: unknown, t: (key: string, options?: any) => string): string => {
  if (isInvalidAmountError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:invalidAmount')
  }

  if (isConflictError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:conflict')
  }

  if (isTimesLimitError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:timesLimit.message')
  }

  if (isExternalDisputeExistError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.externalDisputeExist')
  }

  if (isDuplicatedEntityError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.duplicateEntity')
  }

  if (isCreatingTooEarly((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.tooEarly')
  }

  if (isCreatingTooLate((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.tooLate')
  }

  return t('result:failure:unspecific')
}

export const getDisputeErrorTitle = (error: unknown, t: (key: string, options?: any) => string): string => {
  if (isTimesLimitError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:timesLimit.title')
  }
  return t('result:failure:head')
}

/**
 * Check if an amount meets the Section 75 minimum threshold (over £100)
 * @param amount - The amount as a string or number
 * @returns boolean - True if the amount is over 100
 */
export const isAmountOverSection75Threshold = (amount: string | number): boolean => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numericAmount) || numericAmount <= 0) {
    return false
  }

  return numericAmount > 100
}
